import {Mutable} from 'utility-types';
import {
    Extra,
    HibuDocumentDuplicates,
    HibuHighlightFields,
    HibuHit,
    HibuHitDoc,
    HibuSearchRequest,
    HibuSearchRequestSort,
    HibuSearchRequestSpellcheckParams,
    HibuTag,
    HibuTags,
    Highlights,
    ProductDocument,
    SearchSortType,
    SortType,
    TagMap,
    TagMapEntry,
} from './search-types';
import {generateRandomSortSeed, isSearchInFavorites, localStorageSupported} from '../utils/utils';
import {
    FacetState,
    HibuFacetFilter,
    HibuFilter,
    HibuRangeFilter,
    ReduxHibuFacetValue,
    SelectionType,
} from '../facets/facetTypes';
import {SearchPageTypeState} from './duck/search-types';
import {Language} from '../i18nTypes';
import {SearchPageType} from './search-config';
import {getDownloadFileLink, getPreviewLink, SEARCH_BATCH_SIZE} from '../services/search-service';
import runtimeContext from '../environments/runtime-context';
import {showError} from '../message-box/message-box-actions';
import {GetState} from '../app-types';
import {getCurrentLanguage} from '../i18n-service';


export const BOOKMARKS_API_ENDPOINT = 'bookmarks';
export const HIGHLIGHT_API_ENDPOINT = 'highlightDocument';
export const HISTORY_API_ENDPOINT = 'searchHistory';
export const SAVED_SEARCH_API_ENDPOINT = 'savedSearch';
export const FETCH_DOWNLOAD_FILE_API_ENDPOINT = 'downloadFile';
export const FETCH_PREVIEW_FILE_API_ENDPOINT = 'previewFile';
export const FETCH_PREVIEW_DOCUMENT_API_ENDPOINT = 'previewDocument';
export const HTML_EXPORT = 'htmlexport';
export const JSON_EXPORT = 'jsonexport';

export function getSearchRequest(
    searchPageType: SearchPageType,
    getState: GetState,
    query?: string,
    isUsingAutoCorrection?: boolean): HibuSearchRequest {
    const state = getState();
    const container = state.settings.containerName;
    const language = getCurrentLanguage();
    if (!query) {
        query = state.search[searchPageType].query;
    }
    const docOffset = state.search[searchPageType].docOffset;
    const facetState = state.search[searchPageType].facets;
    const facets = createFacetFilters(facetState);
    const currentSortType: SortType = state.search[searchPageType].currentSortType;
    const defaultSortField = state.search[searchPageType]?.sortTypes?.[0] || undefined;
    const sort = getSort(query, currentSortType, defaultSortField);
    const spellcheckEnabled = !isSearchInFavorites(state.search.BASIC.sources);

    const showFieldSpecificSearch = state.settings.fieldSpecificSearch;
    const queryFields = showFieldSpecificSearch ? state.search[searchPageType].queryFields : undefined;

    return {
        container: container,
        displayLanguage: language,
        query: query,
        from: docOffset,
        size: SEARCH_BATCH_SIZE,
        filters: facets,
        sort: sort,
        spellcheckEnabled: spellcheckEnabled,
        spellcheckParams: getSpellcheckParams(isUsingAutoCorrection),
        queryFields: queryFields,
    };
}

export const isDefaultSortNeeded = (query: string, sortField: string): boolean => {
    return query === '' && sortField.includes(SearchSortType.RELEVANCE.field);
};

/*
behavior (note: the default for currentSortType is relevance):
* empty search + currentSortType set to relevance (score) → default sort (first in list) under the hood but ui shows relevance
* query term not empty + currentSortType set to relevance → relevance
* currentSortType set (from ui or url) to sth else than relevance → as selected resp. set from url
*/
export const getSort = (query: string, currentSortType: SortType, defaultSortType: SortType | undefined): HibuSearchRequestSort => {
    if (defaultSortType && isDefaultSortNeeded(query, currentSortType.field)) {
        return {
            field: defaultSortType.field,
            order: defaultSortType.order,
        };
    } else if (currentSortType.field.includes(SearchSortType.RANDOM.field)) {
        let randomSortSeed: string | null = null;
        if (localStorageSupported()) {
            randomSortSeed = localStorage.getItem('randomSortSeed');
        }
        if (randomSortSeed === null) {
            randomSortSeed = generateRandomSortSeed();
        }
        return {
            seed: randomSortSeed,
            order: SearchSortType.RANDOM.field,
        };
    } else {
        return {
            field: currentSortType.field,
            order: currentSortType.order
        };
    }
};

export const getActualQueryString = (searchState: SearchPageTypeState): string => {
    return searchState.resultFor ? searchState.resultFor : searchState.query;
};

export const isBlacklistedForAutoCorrection = (query: string): boolean => {
    const blacklistForAutoCorrection = ['PMEA'];
    return blacklistForAutoCorrection.some(
        (entry) => entry.localeCompare(query, [], {sensitivity: 'accent'}) === 0
    );
};

export function getSpellcheckParams(isUsingAutoCorrection?: boolean): HibuSearchRequestSpellcheckParams {
    return isUsingAutoCorrection ? {
        spellcheckSuggestionMinDocFreq: 1,
        spellcheckSuggestionMinDocFreqGain: 1,
        autoCorrectionMinDocFreq: 5,
        autoCorrectionMinDocFreqGain: 5,
        autoCorrectionOriginalQueryMaxDocFreq: 1,
    } : {
        spellcheckSuggestionMinDocFreq: 1,
        spellcheckSuggestionMinDocFreqGain: 1,
    };
}

// TODO: simplify logic
export const createFacetFilters = (facetState: FacetState): HibuFacetFilter => {
    const result: HibuFacetFilter = {};
    if (!facetState) {
        throw new Error('facetState is ' + JSON.stringify(facetState));
    }
    facetState.selectedFacetValuesByFieldName.allKeys.forEach((selectedFacetGroupFieldName) => {
        const selectionType = facetState.facetGroups.byKey[selectedFacetGroupFieldName]?.selectionType;
        if (!selectionType) {
            return; // filter not set in ui but from URL
        }

        if (SelectionType.RANGE === selectionType) {
            return (result[selectedFacetGroupFieldName] = createRangeFacetFilter(
                facetState,
                selectedFacetGroupFieldName
            ));
        } else {
            return (result[selectedFacetGroupFieldName] = createFacetFilter(facetState, selectedFacetGroupFieldName));
        }
    });
    return result;
};

const createRangeFacetFilter = (facetState: FacetState, fieldName: string): HibuFilter[] => {
    const filters: HibuFilter[] = [];
    const [lowerBound, upperBound] = facetState.selectedFacetValuesByFieldName.byKey[fieldName] ? facetState.selectedFacetValuesByFieldName.byKey[fieldName] : [];
    const rangeFilter = new HibuRangeFilter(lowerBound, upperBound);
    filters.push(rangeFilter);
    return filters;
};

const createFacetFilter = (facetState: FacetState, fieldName: string): HibuFilter[] => {
    const filters: HibuFilter[] = [];
    facetState.selectedFacetValuesByFieldName.byKey[fieldName]?.forEach(facetValue => {
        const facetValues = facetState.hibuFacetValues.byKey[fieldName] as ReduxHibuFacetValue;
        if (facetValues[facetValue]) { // facetValue may be illegitimate if it comes from a bad URL, for example
            filters.push(facetValues[facetValue].filter);
        }
    });
    return filters;
};

export const mapHighlights = (highlightFields: HibuHighlightFields[]): Mutable<Highlights> => {
    const highlights: Mutable<Highlights> = {};
    if (highlightFields) {
        for (const highlightField of highlightFields) {
            if (highlightField.key === 'contentSource') {
                highlights.contentSource = highlightField.fragments;
            }
            if (highlightField.key === 'title') {
                highlights.title = highlightField.fragments;
            }
            if (highlightField.key === 'content') {
                highlights.content = highlightField.fragments;
            }
        }
    }
    return highlights;
};

export const mapTag = (tag: TagMapEntry, currentLanguage: Language, documentLanguage: Language): HibuTag => {
    return {
        id: tag.tagId,
        label: tag.labels?.[currentLanguage] || tag.labels?.[documentLanguage],
        spans: tag.spans,
        extra: mapExtra(tag.extra),
    };
};

export const mapExtra = (extra: Record<string, any> | undefined): Extra => {
    return {
        score: extra?.score,
        coordinates: extra?.coordinates,
        area: extra?.area,
        aggregated: extra?.aggregationName_tokenized_string !== undefined,
        value: extra?.value_tokenized_string !== undefined ? extra?.value_tokenized_string : undefined,
        userValidated_bool: extra?.userValidated_bool
    };
};

export const mapTagsForDocType = (
    tagTypeKey: string,
    currentLanguage: Language,
    tags: TagMap,
    documentLanguage: Language
): HibuTag[] => {
    return tags[tagTypeKey].map((it) => {
        return mapTag(it, currentLanguage, documentLanguage);
    });
};

export const mapTags = (tags: TagMap, currentLanguage: Language, documentLanguage: Language): HibuTags => {
    const hibuTags: HibuTags = {};
    if (tags) {
        for (const prop in tags) {
            if (Object.prototype.hasOwnProperty.call(tags, prop)) {
                hibuTags[prop] = mapTagsForDocType(prop, currentLanguage, tags, documentLanguage);
            }
        }
    }
    return hibuTags;
};

export function mapDocument(doc: HibuHitDoc, highlightFields: HibuHighlightFields[], duplicates: HibuDocumentDuplicates[]) {
    const tags = doc.tags;
    // @ts-ignore FIXME
    delete doc.tags;

    const mappedDoc: Partial<ProductDocument> = {
        ...doc,
        highlights: mapHighlights(highlightFields),
        hibuTags: mapTags(tags, getCurrentLanguage(), doc.language as Language),
        duplicates: duplicates,
        // @ts-ignore FIXME
        expanded: false, // todo: needed? since they don't exist on the ProductDocument type
    };
    return mappedDoc as ProductDocument;
}

/**
 * Receives the unformatted JSON object from services.
 * It will be reformatted to be used in the product details component.
 *
 * @param docs an array of unformatted products
 * @returns an array of formatted products
 */
export const formatHits = (docs: HibuHit[]): ProductDocument[] => {
    if (!docs) {
        return [];
    }

    return docs.map(({doc, highlightFields}: HibuHit) => {
        return mapDocument(doc, highlightFields, doc.duplicates);
    });
};

export const handleSearchError = (error, dispatch) => {
    if (error.message === '410') {
        runtimeContext.sendRedirect('/error/410?referrer=' + runtimeContext.getLocationPathname());
        return;
    }
    return dispatch(showError(SearchPageType.BASIC, 'search.error.general.title'));
};


/**
 * When getOriginalFile is true, then the original file, that is not indexed, is fetched.
 * If not, then the indexed pdf with the ocr-result is fetched.
 * Return undefined when no link is present at all.
 */
export function extractDownloadLink(
    searchPageType: SearchPageType,
    doc: ProductDocument,
    getOriginalFile: boolean
): string | undefined {
    if (doc.metadata.downloadLink_string) {
        if (getOriginalFile) {
            return getDownloadFileLink(searchPageType, doc.metadata.downloadLink_string);
        } else {
            return getPreviewLink(searchPageType, doc.metadata.downloadLink_string);
        }
    }
}
